import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';

import {
  WorkflowPlan,
  AI_CHAT_CONFIG,
  getWorkflowTaskTheme,
} from '../ai-chat.config';

/**
 * @class ChatWorkflowPlanCardComponent
 * @description A standalone UI component designed to render an orchestrator-generated Workflow Plan
 * step-by-step using a timeline and provides an explicit execution toggle natively tied to NgRx.
 * Dynamically hides completed step descriptions while retaining an accordion expansion toggle.
 */
@Component({
  selector: 'rapider-chat-workflow-plan-card',
  standalone: true,
  imports: [CommonModule, NzTimelineModule, NzButtonModule, NzTagModule],
  templateUrl: './chat-workflow-plan-card.component.html',
})
export class ChatWorkflowPlanCardComponent {
  @Input({ required: true }) plan!: WorkflowPlan;
  @Input() isConfirmed: boolean = false;
  @Output() execute = new EventEmitter<WorkflowPlan>();
  public readonly config = AI_CHAT_CONFIG;

  private router = inject(Router);
  public getTaskTheme = getWorkflowTaskTheme;

  // Reactively tracks which completed tasks have had their descriptions expanded by the user
  public collapsedTasks = signal<Set<string>>(new Set());

  /**
   * Handles the execution click.
   * Emits the event for chat state management, and routes to the execution page
   * passing the complex workflow plan object hidden in the router state.
   */
  public onExecuteClick(): void {
    // 1. Emit to parent so the chat marks it as confirmed/hides the button
    // this.execute.emit(this.plan);

    // 2. Navigate to the headless execution route, passing the plan in the state
    this.router.navigate(['/workflow-execution'], {
      state: { workflowPlan: this.plan },
    });
  }

  /**
   * Toggles the visibility of a completed task's description natively.
   * @param {string} taskId - The unique description string used as the loop track identifier.
   */
  public toggleTaskDetails(taskId: string): void {
    this.collapsedTasks.update((set: Set<string>) => {
      const newSet = new Set(set);
      if (newSet.has(taskId)) newSet.delete(taskId);
      else newSet.add(taskId);
      return newSet;
    });
  }

  public get isAllStepsCompleted(): boolean {
    if (!this.plan || !this.plan.tasks || this.plan.tasks.length === 0)
      return false;
    return this.plan.tasks.every((task) => task.status === 'completed');
  }

  /**
   * Evaluates if a specific task's description should be forced open.
   * @param {string} taskId - The unique description string used as the loop track identifier.
   * @returns {boolean} True if the accordion is collapsed.
   */
  public isTaskCollapsed(taskId: string): boolean {
    return this.collapsedTasks().has(taskId);
  }
}
